import { drinkSpecs } from 'components/MainComponent/drinkSpecs'
import { labelStyles } from 'components/MainComponent/LabelStyles'
import { scaleSqrt } from 'd3-scale'
import { useLabels } from 'Hooks/useLabels'
import { useRef } from 'react'
import styled from 'styled-components'
import { useResizeObserver, useWindowSize } from 'usehooks-ts'

const drinks_abr: { [key: string]: string } = {
  cap: 'cappuccino',
  cre: 'creme',
  esp: 'espresso',
  fil: 'filter',
  ris: 'ristretto',
  sch: 'schale',
  lat: 'latte',
  fla: 'flatwhite'
}
function halfCirclePath (
  radius: number,
  center: { x: number; y: number }
): string {
  const x = center.x - radius
  const y = center.y
  const largeArcFlag = radius > 0 ? 1 : 0
  const sweepFlag = 1

  return `M ${x} ${y} a ${radius} ${radius} 0 ${largeArcFlag} ${sweepFlag} ${
    2 * radius
  } 0`
}

function splitToChunks (array: any[], size: number) {
  var myArray = []
  for (var i = 0; i < array.length; i += size) {
    myArray.push(array.slice(i, i + size))
  }
  return myArray
}

export const DrinksMix = ({
  data,
  userMix,
  totalPaticipants,
  lang
}: {
  data?: { [key: string]: number }
  userMix: string | undefined
  totalPaticipants: number | undefined
  lang: 'de' | 'fr' | undefined
}) => {
  const Labels = useLabels(labelStyles)
  const containerRef = useRef<HTMLDivElement>(null)
  const { width = 0 } = useResizeObserver({
    ref: containerRef,
    box: 'border-box'
  })
  const windowSize = useWindowSize()
  const combinationsPerRow = windowSize.width < 711 ? 4 : 3
  if (!data) return null

  let grouped = Object.entries(data)
    .sort((a: [string, number], b: [string, number]) => b[1] - a[1])
    .slice(0, 6)

  if (grouped.length === 0) return null

  let rows = splitToChunks(
    grouped,
    Math.max(1, Math.round(grouped.length / combinationsPerRow))
  )

  let max = totalPaticipants
    ? Math.round((grouped[0][1] * 100) / totalPaticipants)
    : grouped[0][1]

  const rad = scaleSqrt().domain([0, max]).range([0, 40])
  return (
    <DrinksMixRoot>
      <div>
        {/* <Tail isOpen={isOpen} maxRows={maxRows}> */}
        <Content>
          {grouped.map(([drinks, count], c) => {
            return (
              <Combi ref={containerRef} key={'combi' + c} className='combi'>
                <svg>
                  <path
                    d={halfCirclePath(
                      rad(
                        totalPaticipants
                          ? Math.round((count * 100) / totalPaticipants)
                          : count
                      ),
                      {
                        x: width / 2,
                        y: 50
                      }
                    )}
                    fill={
                      userMix && drinks === userMix
                        ? 'var(--orange-bright-2)'
                        : 'var(--coffee-dark)'
                    }
                  />
                  <line
                    x1={0}
                    x2={width}
                    y1={50}
                    y2={50}
                    stroke='var(--coffee-dark)'
                  />
                </svg>
                <ImageContainer>
                  {drinks.split('_').map((drink, d) => {
                    return (
                      <img
                        key={'drink' + d}
                        src={require('../../assets/icons_' +
                          drinks_abr[drink] +
                          (userMix && drinks === userMix ? '_user' : '') +
                          '.png')}
                        style={{ width: '50px' }}
                      />
                    )
                  })}
                </ImageContainer>
                <div className='lab'>
                  <DrinksList>
                    <Labels className='bold' drinksMix>
                      <b>
                        {' '}
                        {totalPaticipants
                          ? Math.round((count * 100) / totalPaticipants) + '%'
                          : count}
                      </b>
                    </Labels>
                    <Labels key={'drinksmix'} drinksMix>
                      <>
                        {drinks.split('_').map((m, j) => (
                          <>
                            {drinkSpecs[drinks_abr[m]][
                              lang === 'de' ? 'name_de' : 'name_fr'
                            ] + (j < drinks.split('_').length - 1 ? ', ' : '')}
                          </>
                        ))}
                      </>
                    </Labels>
                  </DrinksList>
                </div>
              </Combi>
            )
          })}
        </Content>
      </div>
    </DrinksMixRoot>
  )
}
const Button = styled.button`
  border: 0;
  padding: 10px;
  font-size: 15px;
  background-color: var(--coffee-dark);
  color: var(--text-color);
`

const Content = styled.div`
  display: flex;
  flex-wrap: wrap;
`

const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  min-height: 52px;
  img {
    max-width: calc(100%);
    margin-left: -30px;
  }
`
const DrinksList = styled.div`
  line-height: 1em;
  position: relative;
  z-index: 100;
  width: calc(100% - 40px);
  padding-left: 10px;
  padding-right: 10px;
  color: var(--coffee-color);
  font-size: 15px;
  span.bold {
    display: inline-block;
    width: 100%;
    margin-top: 5px;
    margin-bottom: 5px;
  }
  overflow: hidden;
`
const Combi = styled.div`
  flex: 0 0 33%;
  width: 20%;
  height: 150px;
  text-align: center;
  position: relative;
  overflow: visible;
  @media screen and (max-width: 711px) {
    width: 33%;
    flex: 0 0 33%;
  }
  img {
    position: relative;
    z-index: 100;
  }
  svg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`
const Row = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 30px;
`
const DrinksMixRoot = styled.div`
  width: 100%;
  overflow: visible;
  margin-bottom: 60px;
  /* margin-top: 60px; */
  b {
    font-weight: 900;
  }
  .lab {
    color: var(--coffee-light);
  }

  svg {
    overflow: visible;
    width: 100%;
    height: 50%;
  }
  @media screen and (max-width: 711px) {
    width: calc(100vw - 40px);
  }
`
